import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons'
import JobSearchField from './JobSearchField'
import type { EmployeeAssignmentOption, JobTreadJobOption } from './types'
import { INVENTORY_STATUSES, locationAddressFromJob } from './types'

export type TallyBulkEditValues = {
  assignedEmployeeId?: string
  clearAssignedEmployee?: boolean
  job?: JobTreadJobOption | null
  clearLocation?: boolean
  status?: string
  quantity?: number
  value?: number
  notes?: string
}

type TallyBulkEditModalProps = {
  open: boolean
  isAssetTab: boolean
  selectedCount: number
  employeeOptions: EmployeeAssignmentOption[]
  saving?: boolean
  onClose: () => void
  onApply: (values: TallyBulkEditValues) => void
}

const NO_CHANGE = ''

export const TallyBulkEditModal: React.FC<TallyBulkEditModalProps> = ({
  open,
  isAssetTab,
  selectedCount,
  employeeOptions,
  saving = false,
  onClose,
  onApply,
}) => {
  const [assignedEmployeeId, setAssignedEmployeeId] = useState(NO_CHANGE)
  const [job, setJob] = useState<JobTreadJobOption | null>(null)
  const [clearLocation, setClearLocation] = useState(false)
  const [status, setStatus] = useState(NO_CHANGE)
  const [quantity, setQuantity] = useState('')
  const [value, setValue] = useState('')
  const [notes, setNotes] = useState('')
  const [applyNotes, setApplyNotes] = useState(false)

  useEffect(() => {
    if (!open) return
    setAssignedEmployeeId(NO_CHANGE)
    setJob(null)
    setClearLocation(false)
    setStatus(NO_CHANGE)
    setQuantity('')
    setValue('')
    setNotes('')
    setApplyNotes(false)
  }, [open])

  if (!open) return null

  const handleApply = () => {
    const values: TallyBulkEditValues = {}
    if (assignedEmployeeId === '__clear__') {
      values.clearAssignedEmployee = true
      values.assignedEmployeeId = ''
    } else if (assignedEmployeeId) {
      values.assignedEmployeeId = assignedEmployeeId
    }
    if (clearLocation) {
      values.clearLocation = true
      values.job = null
    } else if (job) {
      values.job = job
    }
    if (!isAssetTab && status) values.status = status
    if (!isAssetTab && quantity.trim() !== '') {
      const n = Number(quantity)
      if (Number.isFinite(n)) values.quantity = n
    }
    if (isAssetTab && value.trim() !== '') {
      const n = Number(value)
      if (Number.isFinite(n)) values.value = n
    }
    if (isAssetTab && applyNotes) values.notes = notes

    const hasChange =
      values.assignedEmployeeId !== undefined ||
      values.clearAssignedEmployee ||
      values.job ||
      values.clearLocation ||
      values.status !== undefined ||
      values.quantity !== undefined ||
      values.value !== undefined ||
      values.notes !== undefined

    if (!hasChange) {
      onClose()
      return
    }
    onApply(values)
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-stretch justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative flex h-[100dvh] max-h-[100dvh] w-full min-h-0 max-w-none flex-col overflow-hidden rounded-none bg-base-100 shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-lg sm:rounded-box"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${selectedCount} items`}
      >
        <div className="relative shrink-0 border-b border-base-200 px-5 pt-5 pb-3">
          <h3 className="font-bold text-xl leading-tight pr-8">Edit {selectedCount} items</h3>
          <p className="text-sm text-base-content/60 mt-1">
            Only fields you change will be applied. Leave others as “No change”.
          </p>
          <button
            type="button"
            className="btn btn-sm btn-circle btn-ghost absolute right-3 top-3"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Assigned employee</span>
            <select
              className="select select-bordered w-full"
              value={assignedEmployeeId}
              onChange={(e) => setAssignedEmployeeId(e.target.value)}
              disabled={saving}
            >
              <option value={NO_CHANGE}>No change</option>
              <option value="__clear__">Clear assignment</option>
              {employeeOptions.map((employee) => (
                <option key={employee.employeeId} value={employee.employeeId}>
                  {employee.displayName} ({employee.employeeId})
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <JobSearchField
              label="Location"
              value={clearLocation ? null : job}
              onChange={(next) => {
                setClearLocation(false)
                setJob(next)
              }}
              placeholder="No change — search to set location…"
              disabled={saving || clearLocation}
            />
            <label className="label cursor-pointer justify-start gap-2 py-0">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={clearLocation}
                onChange={(e) => {
                  setClearLocation(e.target.checked)
                  if (e.target.checked) setJob(null)
                }}
                disabled={saving}
              />
              <span className="label-text text-sm">Clear location</span>
            </label>
            {job && !clearLocation ? (
              <p className="text-xs text-base-content/60">
                Will set location to {locationAddressFromJob(job) || 'selected job'}
              </p>
            ) : null}
          </div>

          {!isAssetTab ? (
            <>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium">Status</span>
                <select
                  className="select select-bordered w-full"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={saving}
                >
                  <option value={NO_CHANGE}>No change</option>
                  {INVENTORY_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium">Quantity</span>
                <input
                  type="number"
                  min={0}
                  className="input input-bordered w-full"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="No change"
                  disabled={saving}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium">Value</span>
                <input
                  type="number"
                  className="input input-bordered w-full"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="No change"
                  disabled={saving}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="label cursor-pointer justify-start gap-2 py-0">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={applyNotes}
                    onChange={(e) => setApplyNotes(e.target.checked)}
                    disabled={saving}
                  />
                  <span className="label-text text-sm font-medium">Update notes</span>
                </label>
                <textarea
                  className="textarea textarea-bordered w-full"
                  value={notes}
                  onChange={(e) => {
                    setApplyNotes(true)
                    setNotes(e.target.value)
                  }}
                  rows={3}
                  placeholder="Leave unchecked for no change"
                  disabled={saving || !applyNotes}
                />
              </div>
              <p className="text-xs text-base-content/60">
                Bulk changes apply to all selected items.
              </p>
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-base-200 px-5 py-3 flex flex-wrap justify-end gap-2 bg-base-100">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleApply} disabled={saving}>
            {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : null}
            {saving ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default TallyBulkEditModal
